import { format } from 'date-fns';
import { CSSProperties, useEffect, useState } from 'react';
import { Box, Button, Row } from '@tempmee/react';
import { OverlappingJobPopup } from '~/commonComponents/popups/OverlappingJobPopup/OverlappingJobPopup';
import { GuideLinesSwitch } from '~/components/Request/multiday/components/Guidelines/GuidelinesSwitch';
import { ShowChildMissingTermsPopup } from '~/components/Request/multiday/components/ShowChildMissingTermsPopup';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { FormErrorBanner, useScrollToError } from '~/components/shared/forms';
import WarningIcon from '~/images/icons/Warning';
import { Colors } from '~/themes/colors';
import { fireReviewConfirmPageViewed } from '~/posthogs/events';
import { ShiftRequestHeader, WizardNavigationGuard } from '../common';
import { ShiftReviewCard } from './ShiftReviewCard';
import { useReviewConfirm } from './useReviewConfirm';

// CSS Grid is required here: flex-wrap cannot replicate `auto-fill` centering because flex items
// in the last row align independently, whereas CSS Grid centers the entire track block as a unit.
const CARD_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, 438px)', // matches the fixed width of ShiftReviewCard
  gap: 16,
  justifyContent: 'center',
  paddingBottom: 60,
};

// The error banner must span all grid columns so it renders above the card grid, not inside a cell.
const BANNER_GRID_SPAN: CSSProperties = { gridColumn: '1 / -1' };

// Standard spacing between a warning icon and the popup title in ConfirmPopup.
// ConfirmPopup renders Icon → h1 with no built-in gap; marginTop on the title bridges it.
const POPUP_ICON_TITLE_GAP: CSSProperties = { marginTop: 20 };

export const ReviewConfirmScreen = () => {
  const {
    officeId,
    sortedDates,
    professionType,
    preferredSpecialty,
    arrivalTime,
    departureTime,
    mealBreakDuration,
    isMealBreakPaid,
    rate,
    isAssistedHygiene,
    shiftOverrides,

    dateToDelete,
    acceptedGuidelines,
    showNavigationGuard,
    showOvertimeLimitPopup,
    showLeavingPopup,
    isPostingShifts,
    isOverlappingJob,
    showJobCreatePaymentError,

    setDateToDelete,
    acceptedGuidelinesControls,
    leavingPopupControls,
    navigationGuardControls,
    overtimeLimitPopupControls,

    handleDeleteDate,
    handleBack,
    handleEditShift,
    handleSendRequest,
    handleConfirmSubmission,
    cancelRequest,

    clearPendingNavigation,
    navigateToPending,

    confirmOverlapping,
    goBackOverlapping,

    hidePaymentError,
    updatePayment,
  } = useReviewConfirm();

  const [bannerVisible, setBannerVisible] = useState(false);
  const bannerRef = useScrollToError(bannerVisible);

  useEffect(() => {
    fireReviewConfirmPageViewed();
  }, []);

  // Auto-clear the banner once the user accepts guidelines
  useEffect(() => {
    if (acceptedGuidelines && bannerVisible) {
      setBannerVisible(false);
    }
  }, [acceptedGuidelines, bannerVisible]);

  const handlePostShifts = () => {
    if (!acceptedGuidelines) {
      setBannerVisible(true);
      return;
    }
    if (isPostingShifts) return;
    handleSendRequest();
  };

  return (
    <ShiftRequestHeader currentStep={3} onBack={handleBack} maxWidth={1120}>
      <Box style={CARD_GRID_STYLE} width="100%">
        {bannerVisible && (
          <div style={BANNER_GRID_SPAN}>
            <FormErrorBanner
              ref={bannerRef}
              visible={bannerVisible}
              message="Please accept GoTu's Shift Guidelines to continue."
            />
          </div>
        )}
        {sortedDates.map(({ date, originalIndex }) => {
          const dateKey = date.toISOString();
          const override = shiftOverrides?.[dateKey];
          return (
            <ShiftReviewCard
              key={dateKey}
              date={date}
              officeId={officeId}
              professionType={professionType}
              specialty={override?.preferredSpecialty ?? preferredSpecialty}
              arrivalTime={override?.arrivalTime ?? arrivalTime}
              departureTime={override?.departureTime ?? departureTime}
              mealBreakDuration={override?.mealBreakDuration ?? mealBreakDuration}
              isMealBreakPaid={override?.isMealBreakPaid ?? isMealBreakPaid}
              rate={override?.rate ?? rate ?? 0}
              isAssistedHygiene={override?.isAssistedHygiene ?? isAssistedHygiene}
              onEdit={() => handleEditShift(originalIndex)}
              onDelete={() => setDateToDelete(date)}
            />
          );
        })}
      </Box>

      <GuideLinesSwitch
        onSwitch={acceptedGuidelinesControls.set}
        hasError={bannerVisible && !acceptedGuidelines}
      />

      <Row gap={4} justify="center">
        <Button
          variant="secondary"
          data-testid="cancel-request-button"
          data-cy="cancel-request-button"
          onClick={leavingPopupControls.setOn}
          size="lg"
        >
          Cancel Request
        </Button>
        <Button
          data-testid="confirm-shifts-button"
          data-cy="confirm-shifts-button"
          disabled={isPostingShifts}
          isLoading={isPostingShifts}
          onClick={handlePostShifts}
          size="lg"
        >
          {`Post ${sortedDates.length} ${sortedDates.length === 1 ? 'Shift' : 'Shifts'}`}
        </Button>
      </Row>

      {dateToDelete && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Delete Shift"
          description={
            <>
              Are you sure you would like to delete the shift for{'\n'}
              <strong>{format(dateToDelete, 'EEE, MMM dd, yyyy')}</strong>?
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setDateToDelete(null)}
          rightButtonText="Delete"
          rightButtonAction={handleDeleteDate}
          closePopup={() => setDateToDelete(null)}
          dataTestId="delete-shift-popup"
        />
      )}

      <WizardNavigationGuard
        isOpen={showNavigationGuard}
        onStay={() => {
          clearPendingNavigation();
          navigationGuardControls.setOff();
        }}
        onLeave={navigateToPending}
      />

      {showOvertimeLimitPopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <WarningIcon width={64} height={64} color={Colors.warning_500} />}
          titleStyle={POPUP_ICON_TITLE_GAP}
          title="Overtime Hours"
          description="The payable hours of this shift exceed your state's daily overtime limit. Your office will be subject to additional charges for any overtime hours worked by a professional."
          leftButtonText="Go Back"
          leftButtonAction={overtimeLimitPopupControls.setOff}
          rightButtonText="Continue"
          rightButtonAction={() => {
            overtimeLimitPopupControls.setOff();
            handleConfirmSubmission();
          }}
          closePopup={overtimeLimitPopupControls.setOff}
          dataTestId="overtime-limit-popup"
        />
      )}

      {showLeavingPopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <WarningIcon width={64} height={64} />}
          titleStyle={POPUP_ICON_TITLE_GAP}
          title="Please Confirm"
          description="Are you sure you would like to cancel this request?"
          leftButtonText="Go Back"
          leftButtonAction={leavingPopupControls.setOff}
          rightButtonText="Confirm"
          rightButtonAction={cancelRequest}
          closePopup={leavingPopupControls.setOff}
          dataTestId="cancel-request-popup"
        />
      )}

      {showJobCreatePaymentError && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => <WarningIcon width={64} height={64} />}
          titleStyle={POPUP_ICON_TITLE_GAP}
          title="Auto-Confirm Disabled"
          description={
            <>
              The auto-confirm feature is only available for offices that have input their payment
              information.
              <br />
              <br />
              You can update your payment information now or continue without payment, and with
              auto-confirm disabled.
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={hidePaymentError}
          rightButtonText="Update"
          rightButtonAction={updatePayment}
          closePopup={hidePaymentError}
          dataTestId="payment-error-popup"
        />
      )}

      <OverlappingJobPopup
        isOpen={!!isOverlappingJob}
        onConfirm={confirmOverlapping}
        onGoBack={goBackOverlapping}
      />

      <ShowChildMissingTermsPopup />
    </ShiftRequestHeader>
  );
};
